import { useQuery } from '@tanstack/react-query';
import { fetchFavorites, type FavoritesData } from '../services/entityApi';
import type { Favorite } from '../services/favoritesStorage';
import type { Logger } from '../types';

export function favoritesQueryKey(userUuid: string | undefined) {
  return ['favorites', userUuid] as const;
}

/**
 * Fetches all favorites for the current user and returns the active entity's slice via `select`.
 * The raw `FavoritesData` blob (all entities) stays in the React Query cache so the mutation
 * can read and merge it without losing other entities' favorites.
 */
export function useFavoritesQuery({
  userUuid,
  token,
  entityId,
  logger,
  enabled = true,
}: {
  userUuid: string | undefined;
  token: string | null | undefined;
  entityId: number | undefined;
  logger: Logger;
  /** Additional gate — pass false to suppress the fetch entirely (e.g. feature-flagged off). */
  enabled?: boolean;
}) {
  return useQuery<FavoritesData, Error, Favorite[]>({
    queryKey: favoritesQueryKey(userUuid),
    queryFn: () => fetchFavorites(userUuid!, token!, logger),
    enabled: enabled && !!userUuid && !!token,
    staleTime: 5 * 60 * 1000,
    retry: false,
    select: (data) => data.favoritesByEntity[String(entityId ?? '')] ?? [],
  });
}
